// apps/vendor/src/pages/auth/LoginPage.tsx
//
// Phase 1 Wave 9 Task 45 — Vendor LoginPage.
//
// Variant rules vs Merchant:
//   - OAuth: Google + Lark (vendors may use either)
//   - Approval flow: surface AUTH_PENDING_APPROVAL message (vendor approval)
//
// Goals:.
// CONTEXT.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  AuthSplitLayout,
Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  describeAuthError,
  useAuth,} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import { LanguageSelector } from '../../components/LanguageSelector.js';
import { useTranslation, t as i18nT } from '../../lib/i18n/index.js';

interface GrecaptchaApi {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
}

function loadRecaptchaScript(siteKey: string): Promise<GrecaptchaApi> {
  if (!siteKey) {
    return Promise.resolve({
      ready: (cb) => {
        cb();
      },
      execute: async () => 'mock-recaptcha-token-bypass-dev',
    });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (w.grecaptcha && typeof w.grecaptcha.execute === 'function') {
    return Promise.resolve(w.grecaptcha as GrecaptchaApi);
  }
  return new Promise<GrecaptchaApi>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-pw-recaptcha]',
    );
    if (existing) {
      existing.addEventListener('load', () => {
        resolve((window as unknown as { grecaptcha: GrecaptchaApi }).grecaptcha);
      });
      existing.addEventListener('error', () => reject(new Error('RECAPTCHA_LOAD_FAILED')));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.pwRecaptcha = '1';
    script.addEventListener('load', () => {
      const api = (window as unknown as { grecaptcha?: GrecaptchaApi }).grecaptcha;
      if (api) resolve(api);
      else reject(new Error('RECAPTCHA_NOT_AVAILABLE'));
    });
    script.addEventListener('error', () => reject(new Error('RECAPTCHA_LOAD_FAILED')));
    document.head.appendChild(script);
  });
}

export function useRecaptcha(siteKey: string): {
  getToken: (action: string) => Promise<string>;
} {
  return useMemo(
    () => ({
      async getToken(action: string): Promise<string> {
        const api = await loadRecaptchaScript(siteKey);
        return new Promise<string>((resolve, reject) => {
          api.ready(() => {
            api
              .execute(siteKey, { action })
              .then(resolve)
              .catch((err: unknown) =>
                reject(err instanceof Error ? err : new Error('RECAPTCHA_EXEC_FAILED')),
              );
          });
        });
      },
    }),
    [siteKey],
  );
}

const LoginSchema = z.object({
  email: z.string().email(i18nT('auth:login.validation.emailInvalid')).max(255),
  password: z
    .string()
    .min(8, i18nT('auth:login.validation.passwordMin'))
    .max(128, i18nT('auth:login.validation.passwordMax')),
});
type LoginInput = z.infer<typeof LoginSchema>;

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
}

function readErrorCode(payload: unknown, status: number): string {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as { success?: boolean }).success === false &&
    'error' in payload
  ) {
    const code = (payload as ErrorEnvelope).error?.code;
    if (typeof code === 'string') return code;
  }
  if (status === 401) return 'AUTH_INVALID_CREDENTIALS';
  if (status === 403) return 'AUTH_RECAPTCHA_FAILED';
  if (status === 423) return 'AUTH_ACCOUNT_LOCKED';
  if (status === 428) return 'AUTH_EMAIL_UNVERIFIED';
  if (status === 429) return 'RATE_LIMIT';
  if (status === 451) return 'AUTH_PENDING_APPROVAL';
  return `HTTP_${status}`;
}

// Wave 12 Task 105 follow-up — local describeError replaced by shared
// describeAuthError from @printway/ui (Task 77). Closes MISS-8 dedup goal.

function useLockoutCountdown(unlockAt: number | null): string | null {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (unlockAt === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [unlockAt]);
  if (unlockAt === null) return null;
  const remaining = Math.max(0, Math.floor((unlockAt - now) / 1000));
  if (remaining <= 0) return null;
  const mm = Math.floor(remaining / 60).toString().padStart(2, '0');
  const ss = (remaining % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

export default function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const fetchMe = useAuth((s) => s.fetchMe);
  const isLoading = useAuth((s) => s.isLoading);
  const { t } = useTranslation('auth');

  const recaptchaSiteKey =
    (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined) ?? '';
  const { getToken } = useRecaptcha(recaptchaSiteKey);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const [serverError, setServerError] = useState<string | null>(null);
  const [lockUntil, setLockUntil] = useState<number | null>(null);
  const [stepUp, setStepUp] = useState<{ challengeToken: string } | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const lockCountdown = useLockoutCountdown(lockUntil);

  const onSubmit = async (values: LoginInput): Promise<void> => {
    setServerError(null);
    setSubmitting(true);
    try {
      const recaptchaToken = await getToken('login');
      const client = apiClient();
      const { data, error, response } = await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown },
          ) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).POST('/api/v1/auth/login', {
        body: {
          email: values.email,
          password: values.password,
          recaptchaToken,
        },
      });

      if (!response.ok) {
        // openapi-fetch puts the error body in `error` (not `data`) on non-2xx.
        // Reading only `data` here dropped the real error code → readErrorCode
        // fell back to the status map (403 → AUTH_RECAPTCHA_FAILED), so a
        // pending-approval vendor saw "reCAPTCHA thất bại" instead of the real
        // "chờ admin duyệt" message. Pass the error body through.
        const code = readErrorCode(data ?? error, response.status);
        let lockedUntil: number | null = null;
        if (code === 'AUTH_ACCOUNT_LOCKED' || response.status === 423) {
          const retryAfter = response.headers.get('retry-after');
          const waitSec = retryAfter ? parseInt(retryAfter, 10) : 15 * 60;
          lockedUntil = Date.now() + waitSec * 1000;
          setLockUntil(lockedUntil);
        }
        setServerError(
          describeAuthError(code, { portal: 'vendor', lockedUntil }),
        );
        return;
      }

      const payload = data as
        | {
            success: true;
            data: {
              outcome: 'authenticated' | 'requires_2fa' | 'step_up';
              method?: 'TOTP' | 'EMAIL';
              challengeToken?: string;
            };
          }
        | undefined;

      const outcome = payload?.data?.outcome;
      if (outcome === 'requires_2fa') {
        const method = (payload?.data?.method ?? 'TOTP').toLowerCase();
        if (payload?.data?.challengeToken) {
          try {
            sessionStorage.setItem('pw_2fa_challenge', payload.data.challengeToken);
          } catch {
            // private mode — verify page handles missing token gracefully.
          }
        }
        navigate(`/verify-2fa?method=${method === 'email' ? 'email' : 'totp'}`);
        return;
      }
      if (outcome === 'step_up' && payload?.data?.challengeToken) {
        setStepUp({ challengeToken: payload.data.challengeToken });
        return;
      }
      await fetchMe();
      navigate('/');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'LOGIN_FAILED';
      setServerError(describeAuthError(message, { portal: 'vendor' }));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitOtp = async (): Promise<void> => {
    if (!stepUp) return;
    if (!/^\d{6}$/.test(otpCode)) {
      setOtpError(t('login.otpError.mustBe6Digits'));
      return;
    }
    setOtpError(null);
    try {
      const client = apiClient();
      const { response } = await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown },
          ) => Promise<{ response: Response }>;
        }
      ).POST('/api/v1/auth/verify-2fa', {
        body: {
          challengeToken: stepUp.challengeToken,
          code: otpCode,
          rememberDevice: false,
        },
      });
      if (!response.ok) {
        setOtpError(t('login.otpError.invalidOrExpired'));
        return;
      }
      await fetchMe();
      navigate('/');
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : 'OTP_VERIFY_FAILED');
    }
  };

  // #2333 — Google OAuth 404: this is a raw browser navigation (<a href>),
  // NOT a csrfFetch call, so it bypasses withApiBase(). On sandbox the vendor
  // vhost has NO `/api/` proxy rule → a bare relative href resolves to the FE
  // origin and 404s. Prepend VITE_API_BASE_URL (same pattern as csrfFetch's
  // withApiBase / merchant-v3 API_BASE_URL) so the browser hits the API host.
  const apiBase = (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
  ).replace(/\/$/, '');
  const oauthGoogleHref = `${apiBase}/api/v1/auth/oauth/google/authorize?portal=vendor`;

  return (
    <AuthSplitLayout variant="marketing">
      {/* UAT B6 (2026-05-20): LanguageSelector available pre-auth so the
          user can flip UI to EN/FR before logging in. Persists via cookie
          (LanguageDetector), no PATCH while anonymous (skipped inside
          LanguageSelector when useAuth().user is null). */}
      <div className="absolute right-4 top-4 z-10">
        <LanguageSelector />
      </div>
      <div className="flex flex-1 items-center justify-center">
      <Card className="w-full max-w-md border-0 shadow-none">
        <CardHeader>
          <CardTitle>{t('login.title')}</CardTitle>
          <CardDescription>
            {t('login.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {lockCountdown ? (
            <div
              role="alert"
              data-testid="lockout-banner"
              className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              {t('login.lockoutBannerPrefix')}{' '}
              <span data-testid="lockout-countdown" className="font-semibold">
                {lockCountdown}
              </span>
              .
            </div>
          ) : null}

          {!stepUp ? (
            <Form onSubmit={handleSubmit(onSubmit)} noValidate>
              <FormField>
                <FormLabel htmlFor="login-email">{t('login.labelEmail')}</FormLabel>
                <FormControl>
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    disabled={!!lockCountdown || submitting || isLoading}
                    {...register('email')}
                  />
                </FormControl>
                {errors.email ? (
                  <FormMessage>{errors.email.message}</FormMessage>
                ) : null}
              </FormField>

              <FormField>
                <FormLabel htmlFor="login-password">{t('login.labelPassword')}</FormLabel>
                <FormControl>
                  <Input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    aria-invalid={errors.password ? 'true' : 'false'}
                    disabled={!!lockCountdown || submitting || isLoading}
                    {...register('password')}
                  />
                </FormControl>
                {errors.password ? (
                  <FormMessage>{errors.password.message}</FormMessage>
                ) : null}
              </FormField>
              {serverError && !lockCountdown ? (
                <p
                  role="alert"
                  data-testid="login-server-error"
                  className="text-sm font-medium text-red-600"
                >
                  {serverError}
                </p>
              ) : null}

              <Button
                type="submit"
                variant="default"
                size="default"
                className="w-full"
                disabled={!!lockCountdown || submitting || isLoading}
                data-testid="login-submit"
              >
                {submitting || isLoading ? t('login.submitting') : t('login.submit')}
              </Button>

              {/* #2358 — Forgot password link was missing on the vendor login
                  screen (2 testers). Uses <a href> to match the register link
                  pattern above. */}
              <p className="mt-2 text-center text-sm">
                <a
                  href="/forgot-password"
                  data-testid="login-forgot-link"
                  className="font-medium text-pw-primary-strong hover:underline"
                >
                  {t('login.forgotLink')}
                </a>
              </p>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-pw-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-pw-bg px-2 text-pw-text-muted">{t('login.orDivider')}</span>
                </div>
              </div>

              <div className="space-y-2">
                <a
                  href={oauthGoogleHref}
                  data-testid="oauth-google-button"
                  className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-pw-border bg-pw-surface px-4 text-sm font-medium text-pw-text transition-colors hover:bg-pw-border/40"
                >
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    fill="currentColor"
                  >
                    <path d="M21.35 11.1H12v3.2h5.35c-.23 1.4-1.66 4.1-5.35 4.1-3.22 0-5.85-2.66-5.85-5.95s2.63-5.95 5.85-5.95c1.84 0 3.07.78 3.78 1.45l2.58-2.49C16.84 3.95 14.66 3 12 3 6.96 3 2.85 7.06 2.85 12.05S6.96 21.1 12 21.1c6.94 0 9.16-4.86 9.16-7.34 0-.5-.05-.94-.16-1.66z" />
                  </svg>
                  {t('login.continueGoogle')}
                </a>
                {/* UAT #2334 — Lark OAuth is admin-portal-only by design
                    (BE trả AUTH_CROSS_ROLE_VIOLATION); nút bị ẩn khỏi vendor. */}
              </div>

              <p className="mt-4 text-center text-sm text-pw-text-muted">
                {t('login.noAccount')}{' '}
                <a
                  href="/register"
                  className="font-medium text-pw-primary-strong hover:underline"
                >
                  {t('login.registerLink')}
                </a>
              </p>
            </Form>
          ) : (
            <div className="space-y-4" data-testid="step-up-otp-section">
              <p className="text-sm text-pw-text">
                {t('login.stepUpDescription')}
              </p>
              <FormField>
                <FormLabel htmlFor="login-stepup-otp">{t('login.stepUpOtpLabel')}</FormLabel>
                <FormControl>
                  <Input
                    id="login-stepup-otp"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    value={otpCode}
                    onChange={(e) =>
                      setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                  />
                </FormControl>
                {otpError ? <FormMessage>{otpError}</FormMessage> : null}
              </FormField>
              <Button
                type="button"
                variant="default"
                className="w-full"
                onClick={() => {
                  void onSubmitOtp();
                }}
                data-testid="step-up-otp-submit"
              >
                {t('login.stepUpConfirm')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setStepUp(null);
                  setOtpCode('');
                  setOtpError(null);
                }}
              >
                {t('login.stepUpCancel')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </AuthSplitLayout>
  );
}
